import React, { useState } from 'react';
import './PilotProgram.css';
import PilotEnrollmentModal from './PilotEnrollmentModal';

const PilotProgram: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCTAClick = (action: string) => {
    console.log(`CTA clicked: ${action}`);
    if (action === 'Start Pilot Program') {
      setIsModalOpen(true);
    } else if (action === 'Download Pilot Brochure') {
      // Handle brochure download
      console.log('Downloading pilot brochure...');
      // You can add actual download logic here
    } else if (action === 'Book a Discussion Call') {
      // Handle booking a call
      console.log('Booking discussion call...');
      // You can add calendar booking logic here
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="pilot-program-page">
      {/* Hero Section */}
      <section className="pilot-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="pilot-headline">
              Launch an Industry‑Ready AI Pilot Program for Your Campus
            </h1>
            <p className="pilot-subheadline">
              Give your Engineering & MCA students hands‑on exposure to Python for AI, 
              Machine Learning, GenAI, and MLOps—through a low‑risk, 8–10 week pilot 
              aligned with academics.
            </p>
            <div className="pilot-cta-buttons">
              <button 
                className="btn-primary"
                onClick={() => handleCTAClick('Start Pilot Program')}
              >
                Start Pilot Program
              </button>
              <button 
                className="btn-secondary"
                onClick={() => handleCTAClick('Download Pilot Brochure')}
              >
                Download Pilot Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1 - Who this pilot is for */}
      <section className="pilot-audience">
        <div className="container">
          <h2>Who the AI Pilot Serves</h2>
          <div className="audience-content">
            <div className="audience-list">
              <div className="audience-item">
                <h3>Engineering students from all branches</h3>
                <p>CSE, IT, ECE, Mechanical, Civil, etc.</p>
              </div>
              <div className="audience-item">
                <h3>MCA students across semesters</h3>
                <p>All semester levels welcome</p>
              </div>
            </div>
            <p className="audience-description">
              AI is no longer limited to computer science; core branches and MCA students 
              now need AI literacy and applied skills to stay competitive in placements 
              and higher studies.
            </p>
            
            <div className="eligibility-box">
              <h3>Eligibility</h3>
              <ul>
                <li>Basic computer knowledge</li>
                <li>Willingness to commit time and complete assignments</li>
                <li>No prior AI / ML experience required</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 - What the pilot covers */}
      <section className="pilot-curriculum">
        <div className="container">
          <h2>Practical, Industry‑Relevant AI Curriculum</h2>
          <p className="curriculum-intro">
            The focus is on doing, not just theory—students learn by building, 
            experimenting, and presenting.
          </p>
          
          <div className="curriculum-grid">
            <div className="curriculum-card">
              <h3>Python for AI</h3>
              <ul>
                <li>Core programming concepts and problem‑solving</li>
                <li>Data handling and basic automation</li>
                <li>Strong foundation for ML and GenAI work</li>
              </ul>
            </div>
            
            <div className="curriculum-card">
              <h3>Machine Learning Fundamentals</h3>
              <ul>
                <li>Supervised and unsupervised learning with real datasets</li>
                <li>Model building, evaluation, and improvement</li>
                <li>Intuition for how ML is used in products</li>
              </ul>
            </div>
            
            <div className="curriculum-card">
              <h3>Generative AI (GenAI)</h3>
              <ul>
                <li>How LLMs and modern GenAI tools work</li>
                <li>Prompting, limitations, and responsible use</li>
                <li>Simple hands‑on GenAI mini‑apps</li>
              </ul>
            </div>
            
            <div className="curriculum-card">
              <h3>Cloud Knowledge (AWS/GCP)</h3>
              <ul>
                <li>Introduction to cloud computing fundamentals</li>
                <li>Hands‑on with AWS/GCP services for AI/ML</li>
                <li>Deploying models and applications on cloud platforms</li>
              </ul>
            </div>
            
            <div className="curriculum-card">
              <h3>Data Engineering</h3>
              <ul>
                <li>Data pipelines and ETL processes</li>
                <li>Working with databases and data warehouses</li>
                <li>Data quality, cleaning, and transformation techniques</li>
              </ul>
            </div>
            
            <div className="curriculum-card">
              <h3>MLOps – Industry Exposure</h3>
              <ul>
                <li>How models move from notebooks to production</li>
                <li>Basics of versioning, deployment, and monitoring</li>
                <li>Big‑picture view of the real‑world AI lifecycle (intro level)</li>
              </ul>
            </div>
          </div>

          <div className="projects-showcase">
            <h3>Projects & Showcases</h3>
            <div className="projects-content">
              <div className="project-item">
                <span className="project-icon">🎯</span>
                <p>Mini and capstone projects mapped to industry use cases</p>
              </div>
              <div className="project-item">
                <span className="project-icon">👥</span>
                <p>Team‑based problem solving and presentations</p>
              </div>
              <div className="project-item">
                <span className="project-icon">📊</span>
                <p>Faculty + industry‑style evaluation rubrics</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - Outcomes & internships */}
      <section className="pilot-outcomes">
        <div className="container">
          <h2>Outcomes Colleges Care About</h2>
          <div className="outcomes-grid">
            <div className="outcomes-main">
              <ul className="outcomes-list">
                <li>Students complete portfolio‑ready AI projects they can show in resumes and interviews.</li>
                <li>Exposure to internship‑style problem statements and deadlines.</li>
                <li>Guidance on:
                  <ul>
                    <li>How to present AI projects in resumes and LinkedIn</li>
                    <li>Framing AI problems in interviews</li>
                    <li>Next steps for higher studies and roles in AI / data</li>
                  </ul>
                </li>
              </ul>
            </div>
            <div className="outcomes-bonus">
              <h3>Where possible, top‑performing students can be:</h3>
              <ul>
                <li>Recommended for internship opportunities (where available)</li>
                <li>Given advanced project mentoring</li>
                <li>Invited to industry interaction sessions with experts</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - Low-risk pilot design */}
      <section className="pilot-design">
        <div className="container">
          <h2>Low‑Risk, High‑Impact Pilot</h2>
          
          <div className="key-message">
            <h3>This pilot is designed to:</h3>
            <div className="message-grid">
              <div className="message-item">
                <span className="message-icon">✅</span>
                <p>Add measurable value to students</p>
              </div>
              <div className="message-item">
                <span className="message-icon">🎯</span>
                <p>Enhance the college's industry alignment</p>
              </div>
              <div className="message-item">
                <span className="message-icon">📚</span>
                <p>Operate without disrupting academics</p>
              </div>
            </div>
            <p className="message-footer">
              It is small, controlled, and outcome‑focused. After the pilot, we jointly 
              review results and then decide whether to scale.
            </p>
          </div>

          <div className="delivery-modes">
            <h3>Modes of Delivery</h3>
            <div className="modes-grid">
              <div className="mode-card">
                <h4>Online</h4>
                <p>Live instructor‑led sessions + recordings</p>
              </div>
              <div className="mode-card">
                <h4>Offline</h4>
                <p>On‑campus classroom / lab sessions</p>
              </div>
              <div className="mode-card recommended">
                <h4>Hybrid (Recommended)</h4>
                <ul>
                  <li>Concept sessions online</li>
                  <li>Hands‑on labs, reviews, and project presentations on campus</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="duration-schedule">
            <div className="duration-info">
              <h3>Duration & Schedule</h3>
              <div className="schedule-options">
                <div className="schedule-item">
                  <h4>Duration:</h4>
                  <p>8–10 weeks</p>
                </div>
                <div className="schedule-item">
                  <h4>Schedule options:</h4>
                  <ul>
                    <li>Weekends, or</li>
                    <li>1–2 weekday evenings per week</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pilot Enrollment Modal */}
      <PilotEnrollmentModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
      />
    </div>
  );
};

export default PilotProgram;